// Quick task command (quicktasks.js): one tap → today's instance in the right project, the clock on it.
// Pure: (ctx, input) → { writes, task, created }. ctx = { tasks, projects, departments, projectTypes, sessions, clock,
// timer, lock, now, newId, departmentMode, typeFor? } — the clock commands' context.
import { listDepartments } from '../hierarchy.js';
import { localToday } from '../time.js';
import { makeTaskDoc } from '../taskdoc.js';
import { quickTasksFor, quickTaskProject } from '../quicktasks.js';
import { set } from './writes.js';
import { bucketWrites, clockIn, switchProject } from './clock.js';
import { startTask } from './timer.js';

const now = (ctx) => (typeof ctx.now === 'function' ? ctx.now() : ctx.now ?? Date.now());

/** Like applyWrites for the task list only: sets + updates, so the next step sees what this one made. */
const withTasks = (ctx, made) => ({ ...ctx, tasks: [...(ctx.tasks || []), ...made] });

/**
 * Start a department's quick task. input = { departmentId, quickTaskId, projectId? } — projectId overrides the
 * job (job scope); home scope always uses the department's home project.
 *   setup / cleanup (bucket): today's checklist for the job (made from its type when missing, else one task), the
 *     timer on its first open item.
 *   anything else: today's open instance (quickTaskId + date + project) reused, else made in the department.
 * Then: clock in, switch project, or start the timer — whichever the clock needs.
 */
export function startQuickTask(ctx, { departmentId, quickTaskId, projectId = null }) {
  const dept = listDepartments(ctx.departments || []).find((d) => d.id === departmentId);
  if (!dept) throw new Error(`no department ${departmentId}`);
  const q = quickTasksFor(dept).find((x) => x.id === quickTaskId);
  if (!q) throw new Error(`${dept.name} has no quick task ${quickTaskId}`);
  const target = q.scope === 'home' ? quickTaskProject(q, dept) : projectId || quickTaskProject(q, dept, { currentProjectId: ctx.clock?.active ? ctx.clock.projectId : null });
  if (!target) throw new Error(q.scope === 'home' ? `${dept.name} has no home project yet — set one in its settings` : `${q.title} goes on a job — clock into one first`);
  const project = (ctx.projects || []).find((p) => p.id === target);
  if (!project) throw new Error(`no project ${target}`);
  const date = localToday();
  const at = now(ctx);
  const writes = [];
  let c = ctx;
  let task = null;
  let created = false;
  const deptId = dept.id === 'general' ? null : dept.id;

  if (q.bucket) {
    const open = () => (c.tasks || []).filter((t) => t.projectId === target && t.bucket === q.bucket && t.bucketDate === date && t.status !== 'done').sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    if (!open().length) {
      const b = bucketWrites({ ...c, departmentMode: c.departmentMode }, { projectId: target, bucket: q.bucket });
      let made = b.created;
      writes.push(...b.writes);
      if (!made.length) {                       // the job has no checklist: one task for the stretch
        const t = makeTaskDoc({ title: q.title, projectId: target, bucket: q.bucket, bucketDate: date, scheduledDate: date, quickTaskId: q.id, ...(deptId ? { departmentId: deptId } : {}) }, c.tasks || [], at, c.newId);
        writes.push(set(`tasks/${t.id}`, t));
        made = [t];
      }
      c = withTasks(c, made);
      created = true;
    }
    task = open()[0] || null;
  } else {
    task = (c.tasks || []).find((t) => t.quickTaskId === q.id && t.projectId === target && t.scheduledDate === date && t.status !== 'done') || null;
    if (!task) {
      task = makeTaskDoc({
        title: q.title, projectId: target, scheduledDate: date, quickTaskId: q.id, tags: q.tags || [],
        ...(q.estimatedMinutes ? { estimatedMinutes: q.estimatedMinutes } : {}), ...(deptId ? { departmentId: deptId } : {}),
      }, c.tasks || [], at, c.newId);
      writes.push(set(`tasks/${task.id}`, task));
      c = withTasks(c, [task]);
      created = true;
    }
  }
  if (!task) throw new Error(`nothing to start for ${q.title}`);
  let clockWrites;
  if (!c.clock?.active) clockWrites = clockIn(c, { projectId: target, taskId: task.id }).writes;
  else if ((c.clock.projectId || null) !== target) clockWrites = switchProject(c, { projectId: target, taskId: task.id }).writes;
  else clockWrites = startTask(c, { taskId: task.id }).writes;
  writes.push(...clockWrites);
  return { writes, task, created, projectId: target, quickTask: q };
}
